import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Star, Users, ArrowRight, MapPin } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { upcomingEvents, historyContent, siteConfig } from '@data/siteData';
import Button from '@components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@components/ui/Card';
import Badge from '@components/ui/Badge';
import { EventsSectionSkeleton } from '@components/loading';
import { useLoading } from '@hooks/useLoading';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import type { Event } from '../types';
import { FadeIn, StaggerContainer, StaggerItem } from '@components/animations';

export default function HomePage() {
  const [events, setEvents] = useState<Event[]>([]);
  const { isLoading: eventsLoading, startLoading, stopLoading } = useLoading({
    initialLoading: true,
  });
  const shouldReduceMotion = useReducedMotion();

  // Simulate async loading of events data
  useEffect(() => {
    startLoading();
    // Simulate API call delay for events
    const timer = setTimeout(() => {
      setEvents(upcomingEvents.slice(0, 3));
      stopLoading();
    }, 500);

    return () => clearTimeout(timer);
  }, [startLoading, stopLoading]);

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 py-20 lg:py-32 overflow-hidden">
        {/* Background decoration */}
        <motion.div 
          initial={shouldReduceMotion ? {} : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: shouldReduceMotion ? 0 : 1 }}
          className="absolute inset-0 overflow-hidden"
        >
          <motion.div 
            animate={shouldReduceMotion ? {} : { 
              scale: [1, 1.1, 1],
              opacity: [0.1, 0.15, 0.1]
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -top-40 -right-40 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" 
          />
          <motion.div 
            animate={shouldReduceMotion ? {} : { 
              scale: [1, 1.2, 1],
              opacity: [0.1, 0.15, 0.1]
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute -bottom-40 -left-40 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl" 
          />
        </motion.div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <StaggerContainer className="text-center max-w-3xl mx-auto" staggerDelay={0.15}>
            <StaggerItem>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600/10 border border-blue-600/20 rounded-full mb-8">
                <Star size={16} className="text-blue-400" />
                <span className="text-blue-300 text-sm font-medium">
                  Seit 1772 – Ein Stück Sternengeschichte
                </span>
              </div>
            </StaggerItem>

            <StaggerItem>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Willkommen in der{' '}
                <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  Alten Sternwarte
                </span>{' '}
                Mannheim
              </h1>
            </StaggerItem>

            <StaggerItem>
              <p className="text-lg sm:text-xl text-slate-400 mb-10 leading-relaxed">
                {siteConfig.tagline}. Erleben Sie Führungen, astronomische Beobachtungen und
                kulturelle Veranstaltungen in einem der ältesten Gebäude Mannheims.
              </p>
            </StaggerItem>

            <StaggerItem>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/termine">
                  <motion.div
                    whileHover={shouldReduceMotion ? {} : { scale: 1.05 }}
                    whileTap={shouldReduceMotion ? {} : { scale: 0.95 }}
                  >
                    <Button size="lg" className="w-full sm:w-auto">
                      <Calendar className="mr-2" size={20} />
                      Termine & Events
                    </Button>
                  </motion.div>
                </Link>
                <Link to="/about">
                  <motion.div
                    whileHover={shouldReduceMotion ? {} : { scale: 1.05 }}
                    whileTap={shouldReduceMotion ? {} : { scale: 0.95 }}
                  >
                    <Button variant="outline" size="lg" className="w-full sm:w-auto">
                      Mehr erfahren
                      <ArrowRight className="ml-2" size={20} />
                    </Button>
                  </motion.div>
                </Link>
              </div>
            </StaggerItem>
          </StaggerContainer>
        </div>
      </section>

      {/* Key Facts Section */}
      <section className="py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {historyContent.keyFacts.map((fact, index) => (
              <FadeIn key={index} delay={index * 0.1} direction="up" distance={20}>
                <motion.div
                  whileHover={shouldReduceMotion ? {} : { y: -4, scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                  className="text-center p-6 bg-slate-900/50 border border-slate-800 rounded-xl cursor-default"
                >
                  <p className="text-slate-400 text-sm mb-2">{fact.label}</p>
                  <p className="text-white font-semibold">{fact.value}</p>
                </motion.div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Events Section */}
      {eventsLoading ? (
        <EventsSectionSkeleton />
      ) : (
        <section className="py-16 bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn direction="left" className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-10">
              <div>
                <h2 className="text-3xl font-bold text-white mb-2">Aktuelle Termine</h2>
                <p className="text-slate-400">
                  Entdecken Sie unsere kommenden Veranstaltungen und Führungen
                </p>
              </div>
              <Link to="/termine">
                <motion.div whileHover={shouldReduceMotion ? {} : { x: 4 }} transition={{ duration: 0.2 }}>
                  <Button variant="outline">
                    Alle Termine
                    <ArrowRight className="ml-2" size={16} />
                  </Button>
                </motion.div>
              </Link>
            </FadeIn>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map((event, index) => (
                <FadeIn key={event.id} delay={index * 0.1} direction="up" distance={30}>
                  <motion.div
                    whileHover={shouldReduceMotion ? {} : { 
                      y: -8, 
                      scale: 1.02,
                      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4), 0 0 30px rgba(59, 130, 246, 0.2)'
                    }}
                    transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
                  >
                    <Card hover>
                      <CardHeader>
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <Badge
                              variant={
                                event.status === 'booked'
                                  ? 'error'
                                  : event.status === 'available'
                                  ? 'success'
                                  : 'default'
                              }
                              className="mb-2"
                            >
                              {event.type}
                            </Badge>
                            <CardTitle className="text-lg">{event.title}</CardTitle>
                          </div>
                        </div>
                        <CardDescription>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              {format(new Date(event.date), 'dd. MMMM yyyy', { locale: de })}
                            </span>
                            <span className="text-slate-400">|</span>
                            <span className="text-slate-300">{event.time}</span>
                          </div>
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-slate-400 text-sm line-clamp-3">{event.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn direction="up" className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Entdecken Sie die Sternwarte</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Die Alte Sternwarte bietet ein vielfältiges Programm für Astronomie-Begeisterte,
              Geschichtsinteressierte und Kulturliebhaber.
            </p>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Star,
                color: 'blue',
                title: 'Astronomische Beobachtungen',
                description: 'Erleben Sie den Sternenhimmel bei unseren Nachtspaziergängen mit erfahrenen Astronom*innen.'
              },
              {
                icon: Users,
                color: 'purple',
                title: 'Führungen',
                description: 'Lernen Sie die Geschichte der Sternwarte kennen und genießen Sie den Ausblick vom Turm über Mannheim.'
              },
              {
                icon: MapPin,
                color: 'amber',
                title: 'Kulturelle Veranstaltungen',
                description: 'Literarische Spaziergänge, Lesungen und Konzerte im einzigartigen Ambiente der Sternwarte.'
              }
            ].map((feature, index) => (
              <FadeIn key={feature.title} delay={index * 0.15} direction="up" distance={40}>
                <motion.div
                  whileHover={shouldReduceMotion ? {} : { 
                    y: -8, 
                    scale: 1.02,
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.3)'
                  }}
                  transition={{ duration: 0.3 }}
                  className="h-full"
                >
                  <Card className="text-center h-full">
                    <CardContent className="pt-8">
                      <motion.div 
                        whileHover={shouldReduceMotion ? {} : { rotate: 10, scale: 1.1 }}
                        transition={{ duration: 0.3 }}
                        className={`w-14 h-14 bg-${feature.color}-600/10 rounded-full flex items-center justify-center mx-auto mb-4`}
                      >
                        <feature.icon className={`text-${feature.color}-400`} size={28} />
                      </motion.div>
                      <CardTitle className="mb-2">{feature.title}</CardTitle>
                      <p className="text-slate-400">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-900/20 to-purple-900/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn direction="up">
            <h2 className="text-3xl font-bold text-white mb-4">Unterstützen Sie die Sternwarte</h2>
            <p className="text-slate-400 mb-8">
              Die Zukunft der Alten Sternwarte steht nun nicht mehr in den Sternen! Helfen Sie mit,
              das historische Gebäude zu erhalten.
            </p>
            <Link to="/unterstuetzen">
              <motion.div
                whileHover={shouldReduceMotion ? {} : { scale: 1.05 }}
                whileTap={shouldReduceMotion ? {} : { scale: 0.95 }}
                className="inline-block"
              >
                <Button size="lg">Jetzt spenden</Button>
              </motion.div>
            </Link>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
